import { useEffect, useState } from "react";
import useApi, { useApiGetter } from "@petsapp/use-api";
import { WellnessPlan } from "../screens/wellness-plans/types";

export type WellnessPlanWithClinic = WellnessPlan & {
  label: string;
  species: string;
  hidden?: boolean;
};

function getSpeciesFromURLSearch() {
  const searchParams = new URLSearchParams(window.location.search);
  const speciesRaw = searchParams.get("species");

  if (!speciesRaw) {
    return [];
  }

  return speciesRaw.split(",").map((item) => item.trim());
}

// this is used to fetch first wellness plan for customised registration view
export function useWellnessPlansBeforeAuth({
  onLoadWellnessPlan,
}: {
  onLoadWellnessPlan: (
    arg0: {
      wellnessPlan?: WellnessPlanWithClinic;
      wellnessPlans: WellnessPlanWithClinic[];
    } | null,
  ) => void;
}) {
  const { makePath } = useApi();
  const [initialised, setInitialised] = useState<boolean>(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const groupId = searchParams.get("groupId");
    const clinicId = searchParams.get("clinicId");

    if (!initialised && (groupId || clinicId)) {
      const apiCallParams = new URLSearchParams();
      if (groupId) {
        apiCallParams.set("groupId", groupId);
      }
      if (clinicId) {
        apiCallParams.set("clinicId", clinicId);
      }
      const path = `/wellness-plans/plans?${apiCallParams.toString()}`;

      (async () => {
        const response = await fetch(makePath(path, 2), {
          headers: {
            "X-Identity": "",
            "X-Metadata": "{}",
          },
        });

        if (response.status === 200) {
          const {
            wellnessPlans,
          }: {
            wellnessPlans: Array<WellnessPlanWithClinic>;
          } = await response.json();
          const speciesFilter = getSpeciesFromURLSearch();
          const plans =
            speciesFilter.length > 0
              ? wellnessPlans.filter((plan) =>
                  speciesFilter?.includes(plan.species),
                )
              : wellnessPlans;
          if (plans.length !== 0) {
            onLoadWellnessPlan({
              wellnessPlan: wellnessPlans.find(
                (plan) => plan.wellnessPlanId === searchParams.get("wp"),
              ),
              wellnessPlans: plans.filter((plan) => !plan.hidden),
            });
          }
          onLoadWellnessPlan(null);
        }
        setInitialised(true);
      })();
    } else {
      setInitialised(true);
    }
  }, [initialised, makePath, onLoadWellnessPlan]);

  return { loading: !initialised };
}

export function useWellnessPlans({
  clinicId,
  groupId,
}: {
  groupId: string;
  clinicId?: string;
}) {
  const apiCallParams = new URLSearchParams();
  if (groupId) {
    apiCallParams.set("groupId", groupId);
  }
  if (clinicId) {
    apiCallParams.set("clinicId", clinicId);
  }

  const { data, loading } = useApiGetter<{
    wellnessPlans: Array<WellnessPlan>;
  }>(`/wellness-plans/plans?${apiCallParams.toString()}`, "NONE", 2);

  return {
    plans: data
      ? data.wellnessPlans.filter((plan: WellnessPlan) => !plan.hidden)
      : [],
    loading,
  };
}
